/**
 * Cart Progress Overlay
 * Displays a floating progress bar showing cart tier thresholds
 */

class CartProgressOverlay {
  constructor(element) {
    this.element = element;
    this.overlay = element;
    this.container = element.querySelector('.cart-progress-overlay__container');
    this.closeButton = element.querySelector('.cart-progress-overlay__close');
    this.messageText = element.querySelector('.cart-progress-overlay__text');
    this.amountSpan = element.querySelector('.cart-progress-overlay__amount');
    this.rewardSpan = element.querySelector('.cart-progress-overlay__reward');
  this.progressFill = element.querySelector('.cart-progress-overlay__segments-fill') ||
            element.querySelector('.cart-progress-overlay__progress-fill');
  this.progressBubble = element.querySelector('.cart-progress-overlay__progress-bubble');
    this.tiersContainer = element.querySelector('.cart-progress-overlay__tiers');
  // In new layout, segments container has this class
  this.segmentsContainer = element.querySelector('.cart-progress-overlay__segments') || this.tiersContainer;
    this.infoSection = element.querySelector('.cart-progress-overlay__info');
  this.progressBar = element.querySelector('.cart-progress-overlay__progress-bar');

    // Settings
    this.delay = parseInt(element.dataset.delay) || 3000;
    this.sessionKey = element.dataset.sessionKey;
    this.successDuration = this.getSuccessDuration();
    this.successMessage = this.getSuccessMessage();
    this.targetCurrency = this.container?.dataset?.targetCurrency || 'GBP';
    
    // State
  this.tiers = this.getTiers();
    this.currentCart = { total: 0, currency: this.targetCurrency };
    this.isVisible = false;
    this.hasReachedMax = false;
    this.displayTimeout = null;
    this.successTimeout = null;

    this.init();
  }

  init() {
    // Check if user closed it in this session
    if (sessionStorage.getItem(this.sessionKey)) {
      return;
    }

    // Apply custom colors from section settings
    this.applyCustomColors();

    // Set up event listeners
    this.setupEventListeners();

    // Set tier count CSS var for thresholds grid
    if (this.container) {
      this.container.style.setProperty('--tier-count', this.tiers.length);
    }

    // Format threshold labels with target currency
    this.formatThresholdLabels();

    // Fetch cart and show overlay after delay
    this.scheduleDisplay();

    // Listen to cart updates
    this.observeCartChanges();
  }

  applyCustomColors() {
    // Colors are passed via inline style variables already from Liquid; keep as-is
  }

  formatThresholdLabels() {
    // Update the "Spend X" labels to use target currency
    const thresholdElements = this.element.querySelectorAll('.cart-progress-overlay__threshold');
    thresholdElements.forEach(el => {
      const amount = parseFloat(el.dataset.thresholdAmount);
      if (amount && !isNaN(amount)) {
        const amountInCents = amount * 100;
        el.textContent = `Spend ${this.formatMoney(amountInCents)}`;
      }
    });
  }

  getTiers() {
    const container = this.segmentsContainer || this.tiersContainer || this.element;
    const tierElements = container.querySelectorAll('.cart-progress-overlay__tier');
    const tiers = Array.from(tierElements).map(tier => ({
      element: tier,
      threshold: parseFloat(tier.dataset.threshold),
      reward: tier.dataset.reward
    }));
    
    // Sort by threshold ascending
    return tiers.sort((a, b) => a.threshold - b.threshold);
  }

  getSuccessDuration() {
    // Try to get from section settings, default to 4 seconds
    const fromAttr = this.container?.dataset?.successDuration;
    if (fromAttr) return parseInt(fromAttr);
    return 4000;
  }

  getSuccessMessage() {
    // Get custom success message from settings
    const message = this.container?.dataset?.successMessage;
    return message || '🎉 Congratulations! You\'ve unlocked {reward}!';
  }

  setupEventListeners() {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }
  }

  scheduleDisplay() {
    this.displayTimeout = setTimeout(() => {
      this.fetchCartAndUpdate();
    }, this.delay);
  }

  async fetchCartAndUpdate() {
    try {
      // Fetch cart in default currency (doesn't affect cart drawer)
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      this.currentCart = {
        total: cart.total_price / 100, // Convert cents to currency units
        totalCents: cart.total_price,
        currency: cart.currency
      };

      this.updateDisplay();
      this.show();
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  formatMoney(cents, currency = null) {
    const amount = (cents / 100).toFixed(2);
    const curr = currency || this.targetCurrency;
    
    // Use Shopify's theme money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      // Get the appropriate money format for the target currency
      const moneyFormats = {
        'GBP': '£{{amount}}',
        'EUR': '€{{amount}}',
        'USD': '${{amount}}',
        'CAD': '${{amount}}',
        'AUD': '${{amount}}'
      };
      const format = moneyFormats[curr] || theme.moneyFormat || '${{amount}}';
      return Shopify.formatMoney(cents, format);
    }
    
    // Fallback formatting
    return `${this.getCurrencySymbol(curr)}${amount}`;
  }

  getCurrencySymbol(currency = null) {
    const curr = currency || this.targetCurrency;
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CAD': '$',
      'AUD': '$',
      'JPY': '¥',
      'CNY': '¥'
    };
    return symbols[curr] || curr + ' ';
  }

  convertToTargetCurrency(amount) {
    // If no conversion needed, return original amount
    if (!this.targetCurrency || this.targetCurrency === this.currentCart.currency) {
      return amount;
    }

    // Use Shopify's Currency converter if available (from currency conversion apps)
    if (typeof Currency !== 'undefined' && Currency.rates && Currency.rates[this.targetCurrency]) {
      const rate = Currency.rates[this.targetCurrency] / (Currency.rates[this.currentCart.currency] || 1);
      return amount * rate;
    }

    // Fallback: If store has EUR as default and target is GBP, use approximate rate
    // This should be set from theme settings ideally, but using a reasonable default
    const conversionRates = {
      'EUR_TO_GBP': 0.86,
      'USD_TO_GBP': 0.79,
      'GBP_TO_EUR': 1.16,
      'GBP_TO_USD': 1.27,
      'EUR_TO_USD': 1.09,
      'USD_TO_EUR': 0.92
    };

    const rateKey = `${this.currentCart.currency}_TO_${this.targetCurrency}`;
    const rate = conversionRates[rateKey] || 1;
    
    return amount * rate;
  }

  updateDisplay() {
    // Convert cart total to target currency for comparison with thresholds
    const cartTotal = this.convertToTargetCurrency(this.currentCart.total);
    const cartTotalCents = Math.round(cartTotal * 100);
    
    const nextTier = this.getNextTier(cartTotal);
    const currentTier = this.getCurrentTier(cartTotal);
    const maxThreshold = this.tiers[this.tiers.length - 1]?.threshold || 0;

    // Update tier states
    this.tiers.forEach(tier => {
      tier.element.classList.remove('is-active', 'is-completed');
      
      if (cartTotal >= tier.threshold) {
        tier.element.classList.add('is-completed');
      } else if (tier === nextTier) {
        tier.element.classList.add('is-active');
      }
    });

    // Global progress across all tiers
    const overallPercentage = maxThreshold > 0 ? Math.min((cartTotal / maxThreshold) * 100, 100) : 0;
    if (this.progressFill) {
      this.progressFill.style.width = `${overallPercentage}%`;
    }
    if (this.progressBubble) {
      const clamped = Math.max(2, Math.min(overallPercentage, 98));
      this.progressBubble.style.left = `${clamped}%`;
      // Display cart total in target currency
      this.progressBubble.textContent = this.formatMoney(cartTotalCents);
    }

    if (nextTier) {

      // Update message - show amount needed in target currency
      const amountAwayCents = (nextTier.threshold - cartTotal) * 100;
      this.amountSpan.textContent = this.formatMoney(amountAwayCents);
      this.rewardSpan.textContent = nextTier.reward;

      // Remove success state
      this.overlay.classList.remove('is-success');
      this.hasReachedMax = false;
    } else if (cartTotal >= this.tiers[this.tiers.length - 1].threshold) {
      // Reached maximum tier - show success
      if (!this.hasReachedMax) {
        this.showSuccess(this.tiers[this.tiers.length - 1].reward);
      }
    } else {
      // Cart is empty or below first tier
      const firstTier = this.tiers[0];
      const amountAwayCents = firstTier.threshold * 100;
      this.amountSpan.textContent = this.formatMoney(amountAwayCents);
      this.rewardSpan.textContent = firstTier.reward;
      this.progressFill.style.width = '0%';
      if (this.progressBubble) {
        this.progressBubble.style.left = '0%';
        this.progressBubble.textContent = this.formatMoney(0);
      }
    }
  }

  getNextTier(cartTotal) {
    return this.tiers.find(tier => cartTotal < tier.threshold);
  }

  getCurrentTier(cartTotal) {
    const completedTiers = this.tiers.filter(tier => cartTotal >= tier.threshold);
    return completedTiers[completedTiers.length - 1] || null;
  }

  showSuccess(reward) {
    this.hasReachedMax = true;
    
    // Update message to congratulations using custom message template
    const message = this.successMessage.replace('{reward}', `<strong>${reward}</strong>`);
    this.messageText.innerHTML = message;
    
    // Add success class to trigger color change
    this.overlay.classList.add('is-success');

    // Auto-hide after duration
    this.successTimeout = setTimeout(() => {
      this.hide();
    }, this.successDuration);
  }

  show() {
    if (this.isVisible) return;
    
    this.isVisible = true;
    this.overlay.classList.add('is-visible');
    this.overlay.classList.remove('is-hidden');
  }

  hide() {
    if (!this.isVisible) return;
    
    this.isVisible = false;
    this.overlay.classList.remove('is-visible');
    this.overlay.classList.add('is-hidden');
  }

  close() {
    // Save to session storage
    sessionStorage.setItem(this.sessionKey, 'true');
    
    // Clear any pending timeouts
    if (this.successTimeout) {
      clearTimeout(this.successTimeout);
    }
    
    this.hide();
  }

  observeCartChanges() {
    // Listen to Shopify cart update events
    document.addEventListener('cart:updated', () => {
      this.fetchCartAndUpdate();
    });

    // Listen to custom cart change events (if your theme uses them)
    document.addEventListener('cart:change', () => {
      this.fetchCartAndUpdate();
    });

    // Fallback: Poll cart periodically (every 5 seconds)
    setInterval(() => {
      if (this.isVisible) {
        this.fetchCartAndUpdate();
      }
    }, 5000);
  }

  destroy() {
    if (this.displayTimeout) {
      clearTimeout(this.displayTimeout);
    }
    if (this.successTimeout) {
      clearTimeout(this.successTimeout);
    }
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', () => this.close());
    }
  }
}

// Initialize all cart progress overlays
document.addEventListener('DOMContentLoaded', () => {
  const overlays = document.querySelectorAll('.cart-progress-overlay');
  overlays.forEach(overlay => {
    new CartProgressOverlay(overlay);
  });
});

// Handle Shopify theme editor
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const overlay = event.target.querySelector('.cart-progress-overlay');
    if (overlay) {
      new CartProgressOverlay(overlay);
    }
  });
}
